import * as React from 'react';
import { Select } from 'antd';
const { Option } = Select;
import { IWidget } from 'common/schema/widget/Widget';
import { SelectInput } from 'client/ui/form/input/SelectInput';
import { isEmpty, isEqual, sortBy, uniq } from 'lodash';
import {
  IWidgetCascadeCombination,
  IWidgetCascadeConfig,
  IWidgetCascadeField
} from './fields/WidgetCascadeField';
import { FormWidgetItem } from '../../form/wrapper/FormWidgetItem';
import { useFormikFieldChange } from 'client/ui/form/hooks/useFormikFieldChange';
import { useFormikContext } from 'formik';
import { IRequest } from 'common/schema/request/Request';

export interface FormWidgetInputCascadeSelectViewProps {
  widget: IWidget;
}

/**
 * Configurazione del singolo campo (field) nel contesto della richiesta (valori)
 * corrente.
 */
export interface IWidgetCascadeRequestField {
  field: IWidgetCascadeField;
  value: any;
  /** Indica se il campo deve apparire come disabilitato o meno nel form */
  disabled: boolean;
  availableCombinations: IWidgetCascadeCombination[];
  options: string[];
}

/**
 * Logica per gestire la selezione a cascata. Si occupa di:
 *  - Calcolare la visibilità dei campi da mostrare e il loro stato (abilitato)
 *  - Reimpostare i campi in seguito alla modifica di una voce precedente
 *  - Gestire le opzioni disponibili alla voce successiva in base alle
 *    opzioni precedenti
 */
function useCascadeSelect(config: IWidgetCascadeConfig | undefined) {
  // Utilizziamo useFormikContext al posto di useRequestCompileContext per evitare
  // la valorizzazione del formik context sbagliato:
  //  - quando si utilizza il widget annidato in lista si lavora su un formik differente rispetto a quello della richiesta principale
  // cosi facendo andiamo a lavorare sul formik corrente
  const { values: formik } = useFormikContext<IRequest>();

  const configFields = sortBy(config?.fields ?? [], 'order');

  /**
   * Se si modifica un valore i-esimo, tutti i valori i+1-esimi vengono
   * reimpostati per garantire la coerenza
   */
  useFormikFieldChange(
    (formik, nexts, prevs) => {
      const changedIdx = nexts.findIndex((n, i) => !isEqual(n, prevs[i])) + 1;
      const resetFields = configFields.slice(changedIdx);
      resetFields.forEach(field => {
        formik.setFieldValue(`values.${field.metadataId}`, '');
        formik.setFieldTouched(`values.${field.metadataId}`, false);
      });
    },
    configFields.map(field => `values.${field.metadataId}`)
  );

  if (!config) return [];

  // I valori selezionati nel form servono per pre-filtrare le opzioni dei
  // passi successivi
  const selectedValues = configFields
    .map(field => ({
      field,
      value: formik.values?.[field.metadataId] as string
    }))
    .filter(v => v.value != null);

  return configFields.reduce((fields, field, i, arr) => {
    let availableCombinations =
      i === 0
        ? config.combinations
        : fields[i - 1].availableCombinations.filter(c => {
            return fields.every(prevField => {
              const prevFieldSelectedValue = selectedValues.find(
                s => s.field.index === prevField.field.index
              );
              return (
                !prevFieldSelectedValue ||
                prevFieldSelectedValue.value === c.values[prevField.field.index]
              );
            });
          });

    fields.push({
      field,
      value: formik.values?.[field.metadataId],
      // Disabilitiamo sempre i campi successivi a quello da configurare per
      // garantire che si possa arrivare a una combinazione valida
      disabled: i === 0 ? false : isEmpty(fields[i - 1].value),
      // Le portiamo appresso solo per essere uste con `fields[i - 1]`
      availableCombinations,
      options: uniq(availableCombinations.map(comb => comb.values[field.index]))
    });
    return fields;
  }, [] as Array<IWidgetCascadeRequestField>);
}

/**
 * Rendering del Widget di input a cascata. Mostra una singola select
 * per ogni colonna.
 */
export function FormWidgetInputCascadeSelectView(
  props: FormWidgetInputCascadeSelectViewProps
) {
  const { widget } = props;

  const cascade = widget.input!.extra?.cascade;
  const fields = useCascadeSelect(cascade);

  if (!cascade) return null;

  return (
    <>
      {fields.map(field => (
        <FormWidgetItem
          key={field.field.index}
          name={`values.${field.field.metadataId}`}
          widget={widget}
          label={field.field.label}
        >
          <SelectInput
            name={`values.${field.field.metadataId}`}
            placeholder={widget.input?.placeholder}
            disabled={field.disabled}
          >
            {field.options.map((option, i) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </SelectInput>
        </FormWidgetItem>
      ))}
    </>
  );
}
